/*
 * tt-reveal.js — blocks fade and rise in as they arrive, instead of snapping.
 *
 * Josh, 2026-08-19: "Lazy load every page. I hate when I go to a page and
 * elements just snap into place when rendering. I way prefer the soft loading
 * vibe when needed."
 *
 * THE SAFETY DESIGN, first, because it is the only part that can break a page.
 * Nothing is hidden by the stylesheet on its own: every rule in tt-reveal.css is
 * gated on `.tt-reveal-ready` on <html>, and this file adds that class. So the
 * order is always mark-then-hide-then-show, never hide-then-hope. A visitor with
 * no JavaScript, a blocked asset, or a parse error gets the ordinary page, fully
 * visible, with no animation. The stylesheet also carries a 3s failsafe
 * animation for the case where this file sets the class and then throws.
 *
 * WHAT IT TARGETS is the page's own block rhythm — the `.tt-block` and `.blk`
 * panels the sections are built from — plus product cards, which are the one
 * repeated element worth staggering. Not every node: a page where everything
 * animates independently reads as jitter rather than as arrival.
 *
 * WHAT IT NEVER TOUCHES: the header and its megamenu, anything fixed or sticky,
 * the cart drawer, and any open dialog. Animating a sticky header on scroll
 * fights the browser, and hiding a drawer that script has just opened is a bug
 * that only appears for one visitor in ten.
 *
 * ABOVE THE FOLD, everything already in view at boot is revealed immediately on
 * the first frame, with a small stagger. Holding the hero back until an observer
 * fires would move Largest Contentful Paint by the length of the animation,
 * which the definition of done forbids — Lighthouse must not drop below stock
 * Dawn. The soft arrival is for what scrolls into view.
 */
(function () {
  'use strict';

  var ROOT = document.documentElement;

  /* Reduced motion and no IntersectionObserver both mean: do nothing at all.
     Returning before the class is added leaves the page in its ordinary state. */
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced && reduced.matches) return;
  if (!('IntersectionObserver' in window)) return;

  var SELECTOR = [
    '.tt-block',
    '.blk',
    '.tt-card',
    '.tt-esplit',
    '[data-tt-reveal-me]',
  ].join(',');

  /* Inside any of these, nothing is marked.

     `[data-tt-results]` is the interesting one. The collection grid ALREADY has
     an arrival choreography - the filter swap fades the region through
     `is-swapped-in`, and infinite scroll gives each appended card its own
     `tt-card--in` with a stagger - and collection-async-check asserts both. A
     second animation layered on the same nodes would fight the first and make
     that check a liar. The grid still arrives softly on first load, because the
     `.tt-block` around it is marked and fades as one. */
  var SKIP_WITHIN = [
    '.tt-header',
    '.tt-mega',
    '.tt-drawer',
    '.tt-cart',
    'dialog',
    '[data-tt-spot]',
    '[data-tt-results]',
  ].join(',');

  var STAGGER = 70;   /* ms between neighbours */
  var MAX_STAGGER = 3; /* …capped, so a long grid has no tail */

  function eligible(el) {
    if (el.closest(SKIP_WITHIN)) return false;
    /* NEVER A BLOCK THAT HOLDS AN APP BLOCK. The reveal's transform makes the
       block the containing box for every position:fixed thing inside it, and an
       app ships its drawers as fixed panels parked off the right edge. With the
       block transformed, "off the right edge" becomes "just right of the panel",
       so on a wide screen the order form's Quick View drawer showed beside the
       page for the length of the fade (Josh, 2026-10-16). The app moves them to
       <body> once its script runs, which is why it was only a flash. */
    if (el.querySelector('.shopify-app-block') || el.closest('.shopify-app-block')) return false;
    var pos = getComputedStyle(el).position;
    if (pos === 'fixed' || pos === 'sticky') return false;
    /* A block nested inside another marked block would animate twice, once with
       its parent and once on its own. The outermost one wins. */
    if (el.parentElement && el.parentElement.closest('[data-tt-reveal]')) return false;
    return true;
  }

  function mark() {
    var out = [];
    var all = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.hasAttribute('data-tt-reveal')) continue;
      if (!eligible(el)) continue;
      el.setAttribute('data-tt-reveal', '');
      out.push(el);
    }
    return out;
  }

  function show(el, index) {
    var d = Math.min(index, MAX_STAGGER) * STAGGER;
    el.style.setProperty('--tt-reveal-delay', d + 'ms');
    el.classList.add('is-in');
    /* Drop the compositor layer once it has arrived. `transitionend` can fire
       per property, so guard on the attribute rather than counting events. */
    var done = function () {
      el.removeAttribute('data-tt-reveal');
      el.style.removeProperty('--tt-reveal-delay');
      el.removeEventListener('transitionend', done);
    };
    el.addEventListener('transitionend', done);
    /* …and unconditionally, because a transition on an element that never
       becomes visible - a collapsed details, a hidden tab - fires nothing. */
    setTimeout(done, 520 + d + 120);
  }

  var io = new IntersectionObserver(function (entries) {
    /* In document order, so a row of cards staggers left to right rather than in
       whatever order the observer happens to batch them. */
    var hits = entries.filter(function (e) { return e.isIntersecting; });
    hits.sort(function (a, b) {
      var p = a.target.compareDocumentPosition(b.target);
      return (p & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1;
    });
    for (var i = 0; i < hits.length; i++) {
      io.unobserve(hits[i].target);
      show(hits[i].target, i);
    }
  }, {
    /* Start it a little before the block's top edge reaches the viewport, so the
       arrival is finishing as it becomes properly readable rather than starting
       then. */
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.01,
  });

  function boot() {
    var fresh = mark();
    if (!fresh.length) return;
    ROOT.classList.add('tt-reveal-ready');

    /* Anything already on screen goes on the next frame. Two frames, in fact:
       one for the browser to apply the hidden state the class just switched on,
       one to transition out of it. Without the second the element has no
       starting value to animate from and it snaps - the exact thing this is
       here to stop. */
    var vh = window.innerHeight || ROOT.clientHeight;
    var above = [];
    var below = [];
    for (var i = 0; i < fresh.length; i++) {
      var r = fresh[i].getBoundingClientRect();
      (r.top < vh ? above : below).push(fresh[i]);
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        for (var i = 0; i < above.length; i++) show(above[i], i);
      });
    });
    for (var j = 0; j < below.length; j++) io.observe(below[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Sections re-rendered through the Section Rendering API - the collection
     grid's filtering and its infinite scroll - bring in blocks this never saw.
     Mark and observe those too, or filtered results arrive by snapping while
     the first page faded. */
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('tt:content-added', boot);
})();

/* REDUCED MOTION STOPS DECORATIVE VIDEO (2026-10-20 launch audit, WCAG 2.2.2). Every muted,
   looping background clip in the theme autoplays. A visitor who has asked their device for less
   motion gets the first frame instead. Caught at the document on 'play' so clips a section
   starts later, after a lazy load, are stopped too. A video with controls is left alone. */
(function () {
  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!mq || !mq.matches) return;
  function still(v) { if (v.tagName === 'VIDEO' && v.muted && v.loop && !v.controls) v.pause(); }
  document.addEventListener('play', function (e) { still(e.target); }, true);
  /* This file is deferred, so a clip may already be playing by now. */
  Array.prototype.forEach.call(document.querySelectorAll('video'), still);
})();
